import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { CheckCircle, Bank as BankIcon, CreditCard, DownloadSimple, UploadSimple, Handshake, SpinnerGap } from '@phosphor-icons/react';
import { ParallaxScene, ParallaxLayer } from '../components/motion/ParallaxScene';
import { TiltCard } from '../components/motion/TiltCard';
import { KitPdfActions } from '../components/adhesion/KitPdfActions';
import { DossierReference } from '../components/adhesion/DossierReference';
import DigitalAdhesionPage from './DigitalAdhesionPage';
const Loader2 = ({ className='', size=16 }) => <SpinnerGap className={`animate-spin ${className}`} size={size} />;

const API = process.env.REACT_APP_BACKEND_URL;

const SITUATIONS = [
  { v: 'salarie', l: 'Salarié·e' },
  { v: 'independant', l: 'Indépendant / entrepreneur' },
  { v: 'demandeur', l: 'Demandeur d\'emploi' },
  { v: 'retraite', l: 'Retraité·e' },
  { v: 'etudiant', l: 'Étudiant·e' },
  { v: 'sans_activite', l: 'Sans activité' },
  { v: 'autre', l: 'Autre' },
];

const CONTRIBUTION_TYPES = [
  'Savoir-faire / compétence', 'Apport en industrie', 'Projet ou marché coopératif',
  'Expertise / ingénierie / coordination', 'Réseau / développement / références',
  'Moyens matériels ou immatériels', 'Formation / accompagnement', 'Autre',
];

const BRANCHES = [
  'BTP / construction', 'Industrie bois', 'Transport / logistique', 'Mobilité personnes',
  'Horticulture / fleuristerie', 'Agro-transformation', 'Commerce / distribution',
  'Immobilier / habitat', 'Formation', 'Conseil / ingénierie / numérique', 'Autre',
];

const COLLEGES_PM = [
  { v: 'G', l: "G — Coopérateurs contributeurs : entreprise ou personne morale contribuant par des biens, services, compétences, moyens, réseaux, références ou projets." },
  { v: 'C', l: "C — Partenaires, collectivités et organismes d'appui : collectivité, institution, financeur, assureur, garant, partenaire ESS ou structure d'accompagnement." },
  { v: 'D', l: "D — Fédérations, organisations professionnelles et monde économique : fédération, syndicat, groupement, organisation professionnelle ou réseau sectoriel." },
  { v: 'F', l: "F — Utilisateurs, bénéficiaires et habitants : personne morale bénéficiant principalement et habituellement des services ou programmes de la coopérative." },
];

const PIECES_LIST_PP = [
  { k: 'identite', l: 'Pièce d\'identité valide', req: true },
  { k: 'domicile', l: 'Justificatif de domicile', req: true },
  { k: 'cv', l: 'CV / parcours', req: true },
  { k: 'qualifications', l: 'Qualifications / diplômes', req: false },
  { k: 'habilitations', l: 'Habilitations utiles', req: false },
  { k: 'references', l: 'Références professionnelles', req: false },
  { k: 'assurances', l: 'Assurances (si requises)', req: false },
  { k: 'attestations', l: 'Attestations fiscales/sociales', req: false },
  { k: 'rib', l: 'RIB', req: false },
];
const PIECES_LIST_PM = [
  { k: 'kbis', l: 'Extrait K-bis (< 3 mois)', req: true },
  { k: 'statuts', l: 'Statuts à jour', req: true },
  { k: 'deliberation', l: "Délibération autorisant l'adhésion", req: true },
  { k: 'rcs_extrait', l: 'Extrait RCS', req: false },
  { k: 'identite', l: 'Pièce d\'identité du dirigeant', req: true },
  { k: 'domicile', l: 'Justificatif d\'adresse (siège)', req: false },
  { k: 'attestations', l: 'Attestations fiscales/sociales', req: false },
  { k: 'assurances', l: 'Assurances (si requises)', req: false },
  { k: 'rib', l: 'RIB de la structure', req: false },
];

export default function PublicAdhesionPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0..6
  const [app, setApp] = useState({
    identity: {}, contribution: { types: [], branches: [] }, declarations: {}, subscription: { nombre_parts: 1 }, acceptance: {},
  });
  const [processing, setProcessing] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/portal/adhesion/${token}`).then((r) => {
      setKit(r.data);
      const initApp = r.data.application ? { ...app, ...r.data.application } : app;
      if (r.data.kit_type === 'associe_pp') {
        initApp.contribution = { ...initApp.contribution, college: 'G' };
      }
      setApp(initApp);
    }).catch(() => toast.error('Lien invalide ou expiré')).finally(() => setLoading(false));
    axios.get(`${API}/api/portal/adhesion/${token}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
    if (params.get('paid') === '1') toast.success('Paiement en cours de traitement…');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const reloadDigitalKit = async () => {
    try {
      const { data } = await axios.get(`${API}/api/portal/adhesion/${token}`);
      setKit(data);
    } catch {
      toast.error('Le dossier est enregistré, mais son actualisation a échoué. Réessayez.');
    }
  };

  const setIdentity = (patch) => setApp((a) => ({ ...a, identity: { ...a.identity, ...patch } }));
  const setContribution = (patch) => setApp((a) => ({ ...a, contribution: { ...a.contribution, ...patch } }));
  const setDeclarations = (patch) => setApp((a) => ({ ...a, declarations: { ...a.declarations, ...patch } }));
  const setSubscription = (patch) => setApp((a) => ({ ...a, subscription: { ...a.subscription, ...patch } }));
  const setAcceptance = (patch) => setApp((a) => ({ ...a, acceptance: { ...a.acceptance, ...patch } }));

  const saveDraft = async () => {
    try { await axios.post(`${API}/api/portal/adhesion/${token}/save-application`, { application: app }); toast.success('Brouillon enregistré'); }
    catch { toast.error('Erreur enregistrement'); }
  };

  const uploadPiece = async (kind, file) => {
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/api/portal/adhesion/${token}/upload-piece?kind=${kind}`, fd);
      setKit((k) => ({ ...k, pieces: { ...(k.pieces || {}), [kind]: { filename: file.name } } }));
      toast.success(`${file.name} téléversé`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur upload'); }
  };

  const submitApp = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/portal/adhesion/${token}/submit-application`, { application: app });
      toast.success('Dossier soumis pour instruction');
      setKit((k) => ({ ...k, status: 'submitted' }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur soumission'); }
    finally { setProcessing(false); }
  };

  const payStripe = async () => {
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/api/portal/adhesion/${token}/pay-stripe`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur Stripe'); setProcessing(false); }
  };
  const payBank = async () => {
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/api/portal/adhesion/${token}/pay-bank`);
      setBankDetails({ bank: r.data.bank, reference: r.data.reference });
      toast.success('Coordonnées bancaires affichées ci-dessous');
      // NB: on ne modifie PAS kit.status ici pour garder la carte agrément (isApproved) montée
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setProcessing(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F5]"><Loader2 className="animate-spin" size={30} /></div>;
  if (!kit) return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F5]" data-testid="public-adhesion-invalid">Lien invalide</div>;
  if (kit.digital_form_key) return <DigitalAdhesionPage kit={kit} token={token} onKitChanged={reloadDigitalKit} />;

  const isPaid = kit.status === 'paid';
  const isSubmitted = ['submitted', 'under_review', 'adjourned', 'refused'].includes(kit.status);
  const isApproved = kit.status === 'approved';
  const parts = Number(app.subscription?.nombre_parts || 1);
  const totalMontant = parts * 375;

  return (
    <div className="min-h-screen bg-[#F9F8F5] py-8 px-4 relative overflow-x-clip" data-testid="public-adhesion-page">
      <ParallaxScene className="pointer-events-none absolute inset-0 -z-0">
        <ParallaxLayer depth={0.05} className="parallax-blob parallax-blob-forest"  style={{ top: '-4%', left: '10%', width: 320, height: 320, opacity: 0.25 }} />
        <ParallaxLayer depth={0.08} className="parallax-blob parallax-blob-cyan"    style={{ top: '20%', right: '6%', width: 260, height: 260, opacity: 0.20 }} />
        <ParallaxLayer depth={0.06} className="parallax-blob parallax-blob-orange" style={{ bottom: '-6%', left: '25%', width: 300, height: 300, opacity: 0.18 }} />
      </ParallaxScene>
      <div className="max-w-3xl mx-auto space-y-6 relative">
        <div className="text-center motion-fade-up motion-fade-up-delay-0">
          <Handshake size={44} className="text-[#2D5A4C] mx-auto" weight="duotone" />
          <h1 className="text-2xl font-bold text-[#1A2421] mt-2">Dossier d'adhésion O'SCOP</h1>
          <p className="text-sm text-[#5E6D66]">{kit.kit_type_label} — {kit.beneficiary?.name}</p>
          <div className="flex justify-center mt-2"><DossierReference reference={kit.dossier_reference} testId="public-dossier-reference" /></div>
          <Badge className="mt-2">{kit.status_label || kit.status}</Badge>
        </div>

        <div className="flex justify-center flex-wrap gap-2" data-testid="public-kit-documents">
          <KitPdfActions kitId={kit.id} token={token} publicAccess />
        </div>
        {kit.official_template && <p className="text-xs text-center text-[#5E6D66] break-words" data-testid="public-kit-official-version">Kit officiel · version {kit.official_template.version} · {kit.official_template.filename}</p>}

        {timeline && timeline.events.length > 0 && (
          <TiltCard max={3} className="motion-fade-up motion-fade-up-delay-1">
          <Card className="border-[#E8E6E1]" data-testid="portal-timeline">
            <CardContent className="p-4">
              <p className="text-xs uppercase text-[#5E6D66] font-semibold mb-3">Suivi de votre dossier</p>
              <div className="space-y-0">
                {timeline.events.map((e, i) => (
                  <div key={e.key} className="flex gap-3" data-testid={`portal-timeline-${e.key}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${i === timeline.events.length - 1 ? 'bg-emerald-500' : 'bg-[#2D5A4C]'}`} />
                      {i < timeline.events.length - 1 && <div className="w-0.5 flex-1 bg-[#E8E6E1] min-h-[20px]" />}
                    </div>
                    <div className="pb-3 flex-1">
                      <p className="text-sm font-semibold text-[#1A2421]">{e.label}</p>
                      <p className="text-xs text-[#5E6D66]">{new Date(e.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}{e.actor ? ` · ${e.actor}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </TiltCard>
        )}

        {isPaid ? (
          <TiltCard max={4} className="motion-fade-up motion-fade-up-delay-2">
          <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-6 text-center space-y-2">
            <CheckCircle size={40} className="text-emerald-600 mx-auto" weight="fill" />
            <p className="font-semibold text-lg">Adhésion finalisée</p>
            <p className="text-sm">Numéro d'associé : <strong data-testid="associate-number">{kit.associate_number || '—'}</strong></p>
            <p className="text-sm">Reçu : <strong>{kit.receipt_number}</strong></p>
            {kit.associate_number && (
              <Button onClick={() => window.open(`${API}/api/portal/adhesion/${token}/certificate`, '_blank')} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="download-certificate">
                <DownloadSimple size={14} className="mr-1" /> Télécharger mon certificat officiel
              </Button>
            )}
          </CardContent></Card>
          </TiltCard>
        ) : isApproved ? (
          <TiltCard max={4} className="motion-fade-up motion-fade-up-delay-2">
          <Card className="border-emerald-200"><CardContent className="p-6 space-y-3">
            <p className="font-semibold text-emerald-700">Votre agrément est prononcé !</p>
            <p className="text-sm">Il vous reste à libérer votre souscription : <strong>{parts} part(s) × 375 € = {totalMontant.toFixed(2)} €</strong></p>
            <div className="flex flex-col md:flex-row gap-2">
              <Button onClick={payStripe} disabled={processing} className="flex-1 bg-[#635BFF] hover:bg-[#4a44d3]" data-testid="pay-stripe">
                <CreditCard size={16} className="mr-1" /> Payer par carte
              </Button>
              <Button onClick={payBank} disabled={processing} variant="outline" className="flex-1" data-testid="pay-bank">
                <BankIcon size={16} className="mr-1" /> Virement bancaire
              </Button>
            </div>
            {bankDetails && (
              <div className="mt-2 p-3 bg-[#F5F4EF] rounded border border-[#E8E6E1]" data-testid="rib-details">
                <p className="text-xs font-semibold mb-2">RIB officiel — Effectuez votre virement</p>
                <div className="text-xs space-y-1">
                  <p><strong>Titulaire :</strong> {bankDetails.bank?.titulaire}</p>
                  <p><strong>IBAN :</strong> <code className="bg-white px-1 rounded">{bankDetails.bank?.iban}</code></p>
                  <p><strong>BIC :</strong> <code className="bg-white px-1 rounded">{bankDetails.bank?.bic}</code></p>
                  {bankDetails.bank?.bic_partenaire && <p><strong>BIC partenaire :</strong> <code>{bankDetails.bank.bic_partenaire}</code></p>}
                  <p><strong>Banque :</strong> {bankDetails.bank?.banque}</p>
                  <p><strong>Référence à indiquer :</strong> <code className="bg-emerald-100 px-1 rounded">{bankDetails.reference}</code></p>
                </div>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => window.open(`${API}/api/portal/adhesion/${token}/rib`, '_blank')} data-testid="download-rib">
                  <DownloadSimple size={14} className="mr-1" /> Télécharger le RIB (PDF)
                </Button>
              </div>
            )}
          </CardContent></Card>
          </TiltCard>
        ) : isSubmitted ? (
          <TiltCard max={3} className="motion-fade-up motion-fade-up-delay-2">
          <Card><CardContent className="p-6 text-center space-y-2">
            <p className="font-semibold">Votre dossier est en cours d'instruction</p>
            <p className="text-xs text-[#5E6D66]">Vous recevrez la décision par email. Vous pourrez ensuite libérer votre part sociale.</p>
          </CardContent></Card>
          </TiltCard>
        ) : (
          <div className="motion-fade-up motion-fade-up-delay-2">
          <Card><CardContent className="p-6 space-y-4">
            <div className="flex flex-wrap justify-center gap-1">
              {['Identité','Contribution','Déclarations','Souscription','Acceptation','Pièces','Récap'].map((lab, i) => (
                <button key={i} onClick={() => setStep(i)} className={`text-[10px] px-2 py-1 rounded ${step===i?'bg-[#2D5A4C] text-white':'bg-[#E8E6E1]'}`} data-testid={`step-btn-${i}`}>{i+1}. {lab}</button>
              ))}
            </div>

            {step === 0 && kit.kit_type === 'associe_pp' && (
              <div className="grid grid-cols-2 gap-3" data-testid="section-identity">
                <div><Label>Civilité</Label><Select value={app.identity.civilite||''} onValueChange={(v)=>setIdentity({civilite:v})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger><SelectContent><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></SelectContent></Select></div>
                <div/>
                <div><Label>Nom *</Label><Input value={app.identity.nom||''} onChange={(e)=>setIdentity({nom:e.target.value})} data-testid="id-nom" /></div>
                <div><Label>Nom de naissance</Label><Input value={app.identity.nom_naissance||''} onChange={(e)=>setIdentity({nom_naissance:e.target.value})}/></div>
                <div><Label>Prénom(s) *</Label><Input value={app.identity.prenoms||''} onChange={(e)=>setIdentity({prenoms:e.target.value})} data-testid="id-prenoms"/></div>
                <div><Label>Date de naissance</Label><Input type="date" value={app.identity.date_naissance||''} onChange={(e)=>setIdentity({date_naissance:e.target.value})}/></div>
                <div><Label>Lieu de naissance</Label><Input value={app.identity.lieu_naissance||''} onChange={(e)=>setIdentity({lieu_naissance:e.target.value})}/></div>
                <div><Label>Nationalité</Label><Input value={app.identity.nationalite||''} onChange={(e)=>setIdentity({nationalite:e.target.value})}/></div>
                <div className="col-span-2"><Label>Adresse complète</Label><Input value={app.identity.adresse||''} onChange={(e)=>setIdentity({adresse:e.target.value})}/></div>
                <div><Label>Code postal / Ville</Label><Input value={app.identity.ville||''} onChange={(e)=>setIdentity({ville:e.target.value})}/></div>
                <div><Label>Téléphone</Label><Input value={app.identity.telephone||''} onChange={(e)=>setIdentity({telephone:e.target.value})}/></div>
                <div><Label>Courriel *</Label><Input type="email" value={app.identity.email||''} onChange={(e)=>setIdentity({email:e.target.value})} data-testid="id-email"/></div>
                <div><Label>Profession</Label><Input value={app.identity.profession||''} onChange={(e)=>setIdentity({profession:e.target.value})}/></div>
                <div><Label>Situation actuelle</Label><Select value={app.identity.situation||''} onValueChange={(v)=>setIdentity({situation:v})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger><SelectContent>{SITUATIONS.map((s)=><SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>SIREN/SIRET (si activité indép.)</Label><Input value={app.identity.siret||''} onChange={(e)=>setIdentity({siret:e.target.value})}/></div>
              </div>
            )}

            {step === 0 && kit.kit_type !== 'associe_pp' && (
              <div className="grid grid-cols-2 gap-3" data-testid="section-identity-pm">
                <div className="col-span-2 bg-[#F5F4EF] p-2 rounded text-xs">
                  <strong>Personne morale</strong> — Merci de renseigner les informations juridiques de votre structure ({kit.kit_type_label}). Le nom du dirigeant sert de signataire du bulletin.
                </div>
                <div className="col-span-2"><Label>Raison sociale / Dénomination *</Label><Input value={app.identity.raison_sociale||''} onChange={(e)=>setIdentity({raison_sociale:e.target.value, nom: e.target.value})} data-testid="pm-raison-sociale"/></div>
                <div><Label>Forme juridique</Label>
                  <Select value={app.identity.forme_juridique||''} onValueChange={(v)=>setIdentity({forme_juridique:v})}>
                    <SelectTrigger data-testid="pm-forme"><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent>
                      {['SAS','SARL','SCIC','SCOP','Association loi 1901','Collectivité territoriale','EPIC','GIE','SA','EURL','SASU','Autre'].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Nom commercial (si différent)</Label><Input value={app.identity.nom_commercial||''} onChange={(e)=>setIdentity({nom_commercial:e.target.value})}/></div>
                <div><Label>SIREN *</Label><Input value={app.identity.siren||''} onChange={(e)=>setIdentity({siren:e.target.value})} data-testid="pm-siren" placeholder="9 chiffres"/></div>
                <div><Label>SIRET (établissement principal)</Label><Input value={app.identity.siret||''} onChange={(e)=>setIdentity({siret:e.target.value})} placeholder="14 chiffres"/></div>
                <div><Label>Code APE / NAF</Label><Input value={app.identity.ape||''} onChange={(e)=>setIdentity({ape:e.target.value})}/></div>
                <div><Label>RCS (ville d'immatriculation)</Label><Input value={app.identity.rcs||''} onChange={(e)=>setIdentity({rcs:e.target.value})} data-testid="pm-rcs" placeholder="Ex : RCS Pointe-à-Pitre"/></div>
                <div><Label>Capital social (€)</Label><Input value={app.identity.capital_social||''} onChange={(e)=>setIdentity({capital_social:e.target.value})}/></div>
                <div><Label>Date de constitution</Label><Input type="date" value={app.identity.date_constitution||''} onChange={(e)=>setIdentity({date_constitution:e.target.value})}/></div>
                <div className="col-span-2"><Label>Adresse du siège social *</Label><Input value={app.identity.adresse||''} onChange={(e)=>setIdentity({adresse:e.target.value})}/></div>
                <div><Label>Code postal / Ville</Label><Input value={app.identity.ville||''} onChange={(e)=>setIdentity({ville:e.target.value})}/></div>
                <div><Label>Pays</Label><Input value={app.identity.pays||'France'} onChange={(e)=>setIdentity({pays:e.target.value})}/></div>
                <p className="col-span-2 text-xs font-semibold text-[#2D5A4C] mt-2">Représentant légal (signataire du bulletin)</p>
                <div><Label>Civilité</Label><Select value={app.identity.civilite||''} onValueChange={(v)=>setIdentity({civilite:v})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger><SelectContent><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></SelectContent></Select></div>
                <div><Label>Qualité</Label><Input value={app.identity.qualite_dirigeant||''} onChange={(e)=>setIdentity({qualite_dirigeant:e.target.value})} placeholder="Président, gérant, DG…"/></div>
                <div><Label>Prénom(s) du dirigeant *</Label><Input value={app.identity.prenoms||''} onChange={(e)=>setIdentity({prenoms:e.target.value})} data-testid="id-prenoms"/></div>
                <div><Label>Nom du dirigeant</Label><Input value={app.identity.nom_dirigeant||''} onChange={(e)=>setIdentity({nom_dirigeant:e.target.value})}/></div>
                <div><Label>Téléphone</Label><Input value={app.identity.telephone||''} onChange={(e)=>setIdentity({telephone:e.target.value})}/></div>
                <div><Label>Courriel de contact *</Label><Input type="email" value={app.identity.email||''} onChange={(e)=>setIdentity({email:e.target.value})} data-testid="id-email"/></div>
                <div className="col-span-2 bg-blue-50 border border-blue-200 p-2 rounded text-[11px]">
                  📎 Pensez à téléverser à l'étape « Pièces » : un extrait K-bis récent (moins de 3 mois), les statuts à jour, et la délibération autorisant l'adhésion.
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-3" data-testid="section-contribution">
                {kit.kit_type === 'associe_pp' ? (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded" data-testid="pp-college-fixed">
                    <p className="text-xs font-semibold text-emerald-900">COLLÈGE D'AFFECTATION</p>
                    <p className="text-xs mt-1">Le candidat personne physique sollicitant son admission en qualité d'associé coopérateur contributeur est rattaché au :</p>
                    <p className="text-sm font-bold mt-2">COLLÈGE G — COOPÉRATEURS CONTRIBUTEURS</p>
                    <p className="text-[10px] text-[#5E6D66] mt-1">Pondération statutaire du collège : 10 %.</p>
                    <p className="text-[10px] text-[#5E6D66]">Cette affectation résulte du parcours d'admission retenu pour les associés coopérateurs contributeurs personnes physiques.</p>
                  </div>
                ) : (
                  <div className="p-3 bg-[#F5F4EF] rounded" data-testid="pm-college-choice">
                    <p className="text-xs font-semibold mb-2">CATÉGORIE DE CONTRIBUTION — COLLÈGE PROPOSÉ</p>
                    <div className="space-y-1">
                      {COLLEGES_PM.map((c) => (
                        <label key={c.v} className="flex items-start gap-2 text-[11px] cursor-pointer p-1 hover:bg-white rounded">
                          <input type="radio" name="college_pm" checked={app.contribution.college === c.v} onChange={() => setContribution({ college: c.v })} className="mt-0.5" data-testid={`pm-college-${c.v}`} />
                          <span>{c.l}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#5E6D66] mt-2 italic">Le collège retenu après instruction O'SCOP figurera sur votre certificat d'associé.</p>
                  </div>
                )}
                <div><Label>Nature de contribution envisagée</Label>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    {CONTRIBUTION_TYPES.map((t)=>(
                      <label key={t} className="flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={(app.contribution.types||[]).includes(t)} onChange={(e)=>{ const list=new Set(app.contribution.types||[]); e.target.checked?list.add(t):list.delete(t); setContribution({types:Array.from(list)}); }} data-testid={`ct-type-${t.slice(0,4)}`}/> {t}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Métier / domaine</Label><Input value={app.contribution.metier||''} onChange={(e)=>setContribution({metier:e.target.value})}/></div>
                  <div><Label>Qualifications</Label><Input value={app.contribution.qualifications||''} onChange={(e)=>setContribution({qualifications:e.target.value})}/></div>
                  <div><Label>Habilitations</Label><Input value={app.contribution.habilitations||''} onChange={(e)=>setContribution({habilitations:e.target.value})}/></div>
                  <div><Label>Moyens</Label><Input value={app.contribution.moyens||''} onChange={(e)=>setContribution({moyens:e.target.value})}/></div>
                  <div><Label>Disponibilités</Label><Input value={app.contribution.disponibilites||''} onChange={(e)=>setContribution({disponibilites:e.target.value})}/></div>
                </div>
                <div><Label>Expériences / références</Label><Textarea value={app.contribution.experiences||''} onChange={(e)=>setContribution({experiences:e.target.value})} rows={3}/></div>
                <div><Label>Description détaillée de la contribution</Label><Textarea value={app.contribution.description||''} onChange={(e)=>setContribution({description:e.target.value})} rows={3} data-testid="ct-description"/></div>
                <div><Label>Branches d'intérêt</Label>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    {BRANCHES.map((b)=>(
                      <label key={b} className="flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={(app.contribution.branches||[]).includes(b)} onChange={(e)=>{ const list=new Set(app.contribution.branches||[]); e.target.checked?list.add(b):list.delete(b); setContribution({branches:Array.from(list)}); }}/> {b}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-2" data-testid="section-declarations">
                {[
                  ['certifie_exactes', 'Je certifie exactes les informations et pièces communiquées.'],
                  ['signale_changement', 'Je m\'engage à signaler tout changement affectant ma contribution, assurances, habilitations ou disponibilité.'],
                  ['declare_conflits', 'Je déclare tout conflit d\'intérêts connu ou potentiel.'],
                  ['reconnait_reglementations', 'Je reconnais que les activités réglementées exigent les autorisations et assurances requises.'],
                  ['reconnait_pas_auto_mission', 'Je reconnais que l\'admission ne vaut pas automatiquement mission ou rémunération.'],
                  ['reconnait_qualification', 'Je reconnais que toute intervention future fera l\'objet d\'une qualification et, si nécessaire, d\'un acte distinct.'],
                ].map(([k, l]) => (
                  <label key={k} className="flex gap-2 text-xs items-start">
                    <input type="checkbox" checked={!!app.declarations[k]} onChange={(e)=>setDeclarations({[k]:e.target.checked})} className="mt-0.5" data-testid={`decl-${k}`}/>
                    <span>{l}</span>
                  </label>
                ))}
                <div className="pt-2"><Label>Conflits d'intérêts / observations</Label><Textarea value={app.declarations.observations||''} onChange={(e)=>setDeclarations({observations:e.target.value})} rows={2}/></div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3" data-testid="section-subscription">
                <div className="p-3 bg-[#F5F4EF] rounded"><p className="text-xs">Valeur unitaire d'une part sociale : <strong>375,00 €</strong></p></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nombre de parts</Label><Input type="number" min="1" value={app.subscription.nombre_parts||1} onChange={(e)=>setSubscription({nombre_parts:Number(e.target.value)||1})} data-testid="sub-parts"/></div>
                  <div><Label>Mode de paiement souhaité</Label>
                    <Select value={app.subscription.mode||'virement'} onValueChange={(v)=>setSubscription({mode:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="virement">Virement bancaire</SelectItem><SelectItem value="carte">Carte bancaire (Stripe)</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded"><p className="text-sm">Montant total : <strong>{totalMontant.toFixed(2)} €</strong></p></div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-2" data-testid="section-acceptance">
                {[
                  ['statuts', 'J\'ai reçu, consulté ou pu consulter les statuts O\'SCOP en vigueur et j\'en accepte les dispositions.'],
                  ['reglement', 'J\'ai reçu, consulté ou pu consulter le règlement intérieur général et j\'en accepte les dispositions.'],
                  ['decisions_collectives', 'Je m\'engage à respecter les décisions collectives, règles de confidentialité, sécurité et conformité.'],
                  ['courriel_convocations', 'J\'accepte l\'usage de mon courriel pour les convocations et informations sociales.'],
                  ['vote_college', 'Je comprends que mes droits de vote sont ceux du collège auquel je serai affecté·e.'],
                ].map(([k, l]) => (
                  <label key={k} className="flex gap-2 text-xs items-start"><input type="checkbox" checked={!!app.acceptance[k]} onChange={(e)=>setAcceptance({[k]:e.target.checked})} className="mt-0.5" data-testid={`acc-${k}`}/><span>{l}</span></label>
                ))}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div><Label>Fait à</Label><Input value={app.acceptance.fait_a||''} onChange={(e)=>setAcceptance({fait_a:e.target.value})}/></div>
                  <div><Label>Le</Label><Input type="date" value={app.acceptance.date||''} onChange={(e)=>setAcceptance({date:e.target.value})}/></div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-2" data-testid="section-pieces">
                <p className="text-xs text-[#5E6D66]">Téléversez vos pièces justificatives (PDF, images, max 10 Mo)</p>
                {(kit.kit_type === 'associe_pp' ? PIECES_LIST_PP : PIECES_LIST_PM).map((p) => (
                  <div key={p.k} className="flex items-center gap-2 p-2 border border-[#E8E6E1] rounded">
                    <span className="text-xs flex-1">{p.l}{p.req && <span className="text-red-500"> *</span>}</span>
                    {kit.pieces?.[p.k] ? <Badge className="bg-emerald-100 text-emerald-700">{kit.pieces[p.k].filename}</Badge> : <span className="text-[10px] text-[#5E6D66]">—</span>}
                    <label className="cursor-pointer">
                      <UploadSimple size={16} className="text-[#2D5A4C]" />
                      <input type="file" hidden onChange={(e)=>{const f=e.target.files?.[0]; if(f) uploadPiece(p.k, f);}} data-testid={`upload-${p.k}`}/>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {step === 6 && (
              <div className="space-y-3" data-testid="section-recap">
                <p className="text-sm font-semibold">Récapitulatif — {app.identity.nom} {app.identity.prenoms}</p>
                <div className="text-xs space-y-1">
                  <p>Email : {app.identity.email}</p>
                  <p>Collège : {app.contribution.college || 'G'}</p>
                  <p>Parts : {parts} × 375 € = <strong>{totalMontant.toFixed(2)} €</strong></p>
                  <p>Pièces téléversées : {Object.keys(kit.pieces || {}).length}</p>
                </div>
                <Button onClick={submitApp} disabled={processing} className="w-full bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="submit-application">
                  {processing && <Loader2 size={14} className="mr-1 animate-spin"/>} Soumettre mon dossier pour instruction
                </Button>
                <p className="text-[10px] text-[#5E6D66] text-center">Vous recevrez la décision d'agrément par email.</p>
              </div>
            )}

            <div className="flex flex-wrap justify-between gap-2 pt-3 border-t border-[#E8E6E1]">
              <Button variant="outline" size="sm" onClick={() => setStep(Math.max(0, step-1))} disabled={step===0} data-testid="step-previous">Précédent</Button>
              <Button size="sm" variant="ghost" onClick={saveDraft} data-testid="save-application-draft">Enregistrer brouillon</Button>
              <Button size="sm" onClick={() => setStep(Math.min(6, step+1))} disabled={step===6} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="step-next">Suivant</Button>
            </div>
          </CardContent></Card>
          </div>
        )}
      </div>
    </div>
  );
}
